import { useMutation, useQueryClient } from '@tanstack/react-query';

import { markFeatureSeen } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import { FEATURE_RECORD_QUERY_KEY } from 'api/queries/useFeatureRecordQuery';
import logError from 'utils/errorUtils';

/**
 * Hook for marking a feature as seen by the user.
 * @returns {{ markAsSeen: Function }}
 */
const useMarkFeatureSeen = () => {
    const queryClient = useQueryClient();
    const { data: account } = useAccountQuery();
    const subscriptionID = account?.subID;

    const mutation = useMutation({
        mutationFn: async ({ featureKey }) => {
            if (!subscriptionID) {
                throw new Error('Missing subscription ID');
            }
            return markFeatureSeen(subscriptionID, { feature: featureKey });
        },
        onMutate: ({ featureKey }) => {
            // Abort in-flight feature-record fetches before updating cache. Otherwise a refetch
            // that resolves after this handler starts could overwrite the seen marker with stale
            // data. Do not await (avoids a render gap where the modal effect reopens).
            // revert: false + silent: true avoids reverting to pre-fetch unseen state or error UI.
            queryClient.cancelQueries(
                { queryKey: [FEATURE_RECORD_QUERY_KEY, subscriptionID] },
                { revert: false, silent: true }
            );

            const previousFeatureRecord = queryClient.getQueryData([FEATURE_RECORD_QUERY_KEY, subscriptionID]);
            queryClient.setQueryData([FEATURE_RECORD_QUERY_KEY, subscriptionID], (old) => {
                if (!old || typeof old !== 'object') return old;
                return {
                    ...old,
                    [featureKey]: new Date().toISOString(),
                };
            });

            return { previousFeatureRecord };
        },
        onSuccess: () => {
            // Invalidate to ensure we have the server's actual value
            queryClient.invalidateQueries({ queryKey: [FEATURE_RECORD_QUERY_KEY] });
        },
        onError: (err, variables, context) => {
            // Rollback to previous value on error
            if (context?.previousFeatureRecord) {
                queryClient.setQueryData([FEATURE_RECORD_QUERY_KEY, subscriptionID], context.previousFeatureRecord);
            }
            logError(err);
            console.error('Error marking feature as seen:', err);
        },
    });

    /**
     * Mark a feature as seen.
     * @param {string} featureKey - The key identifying the feature being marked as seen.
     * @param {Object} [mutationOptions] - Optional TanStack Query mutation options.
     */
    const markAsSeen = (featureKey, mutationOptions) => {
        mutation.mutate({ featureKey }, mutationOptions);
    };

    return { markAsSeen };
};

export default useMarkFeatureSeen;
